// SPDX-License-Identifier: AGPL-3.0-or-later

import '@app/features/daoi/components/DaoI.css';
import {Routes} from '@app/app/Routes';
import {privyAppId} from '@app/features/auth/privy/PrivyMode';
import {DaoIBusinessHost} from '@app/features/daoi/bridge/DaoIBusinessHost';
import {DaoIWallet} from '@app/features/daoi/bridge/DaoIWallet';
import {type DaoICommunity, DaoIContext, type DaoIMe, getDaoIData, useDaoI} from '@app/features/daoi/state/DaoIData';
import Guilds from '@app/features/guild/state/Guilds';
import * as RouterUtils from '@app/features/navigation/utils/RouterUtils';
import {useLocation} from '@app/features/platform/components/router/RouterReact';
import {AccountScopedWork} from '@app/features/platform/state/AccountScopedWork';
import {http} from '@app/features/platform/transport/RestTransport';
import Theme from '@app/features/theme/state/Theme';
import Users from '@app/features/user/state/Users';
import {ThemeTypes} from '@fluxer/constants/src/UserConstants';
import {observer} from 'mobx-react-lite';
import {type ReactNode, useCallback, useEffect, useRef, useState} from 'react';

export const DAOI_ROUTES = {mint: '/daoi/mint', swap: '/daoi/swap', me: '/daoi/me', partner: '/daoi/partner'} as const;
export type DaoIBusinessView = 'mint' | 'swap' | 'partner';

export function isDaoIModulePath(pathname: string): boolean {
	return Object.values(DAOI_ROUTES).some((path) => path === pathname);
}

// Data only: the original AppLayout, GuildsLayout, DMLayout and UserArea own the UI.
export const DaoIWorkspaceProvider = observer(({children}: {children: ReactNode}) => {
	return <DaoIWorkspaceSession key={Users.currentUser?.id ?? 'anonymous'}>{children}</DaoIWorkspaceSession>;
});

const DaoIWorkspaceSession = observer(({children}: {children: ReactNode}) => {
	const userId = Users.currentUser?.id;
	const accountWorkAdmitted = !AccountScopedWork.isSuspended;
	const location = useLocation();
	const lastMessagePath = useRef<string>(Routes.ME);
	useEffect(() => {
		if (location.pathname.startsWith('/channels/')) lastMessagePath.current = location.pathname;
	}, [location.pathname]);
	const openMessages = useCallback(() => RouterUtils.transitionTo(lastMessagePath.current), []);
	const [me, setMe] = useState<DaoIMe | null>(null);
	const [communities, setCommunities] = useState<Array<DaoICommunity>>([]);
	const [communitiesLoaded, setCommunitiesLoaded] = useState(false);
	const [pinnedGuildIds, setPinnedGuildIds] = useState<Array<string>>([]);
	const pins = useRef({
		revision: 0,
		pending: 0,
		active: true,
		generation: 0,
		tail: Promise.resolve(),
	});
	useEffect(() => {
		const state = pins.current;
		state.active = true;
		return () => {
			state.active = false;
			state.generation++;
		};
	}, []);
	const applyPinRead = (ids: Array<string>, revision: number | null) => {
		const state = pins.current;
		if (revision === null || revision !== state.revision || state.pending > 0) return;
		setPinnedGuildIds(ids);
	};
	const [refreshCount, setRefreshCount] = useState(0);
	const guildIds = Guilds.getGuildIds().sort().join(',');
	const previousGuildIds = useRef(guildIds);
	const refresh = useCallback(() => setRefreshCount((count) => count + 1), []);
	const openBusiness = useCallback((view: DaoIBusinessView) => RouterUtils.transitionTo(DAOI_ROUTES[view]), []);

	useEffect(() => {
		if (!userId || !accountWorkAdmitted) return;
		const controller = new AbortController();
		setMe(null);
		DaoIWallet.setBoundAddress(null);
		void getDaoIData<{required: boolean; linked: boolean; wallet_address: string | null}>(
			'/auth/sso/wallet/status',
			controller.signal,
		)
			.then(async (status) => {
				if (controller.signal.aborted) return;
				DaoIWallet.setBoundAddress(status.wallet_address);
				if (status.required && !status.linked) return;
				if (!privyAppId()) void DaoIWallet.restore();
				const pinRevision = pins.current.pending === 0 ? pins.current.revision : null;
				const results = await Promise.allSettled([
					getDaoIData<DaoIMe>('/daoi/me', controller.signal),
					getDaoIData<{communities: Array<DaoICommunity>}>('/daoi/communities', controller.signal),
					getDaoIData<{pinned_guild_ids: Array<string>}>('/daoi/conversation-preferences', controller.signal),
				]);
				if (controller.signal.aborted) return;
				setMe(
					results[0].status === 'fulfilled'
						? results[0].value
						: {
								wallet_address: status.wallet_address,
								partner: {eligible: null, status: 'unavailable'},
								partner_status: 'unavailable',
							},
				);
				if (results[1].status === 'fulfilled') {
					setCommunities(results[1].value.communities);
					setCommunitiesLoaded(true);
				}
				if (results[2].status === 'fulfilled') applyPinRead(results[2].value.pinned_guild_ids, pinRevision);
			})
			.catch(() => {
				if (!controller.signal.aborted)
					setMe({
						wallet_address: null,
						partner: {eligible: null, status: 'unavailable'},
						partner_status: 'unavailable',
					});
			});
		return () => controller.abort();
	}, [userId, refreshCount, accountWorkAdmitted]);

	useEffect(() => {
		if (!me?.wallet_address || !accountWorkAdmitted) return;
		let controller: AbortController | null = null;
		const reconcile = () => {
			controller?.abort();
			controller = new AbortController();
			const signal = controller.signal;
			const pinRevision = pins.current.pending === 0 ? pins.current.revision : null;
			void Promise.allSettled([
				getDaoIData<{communities: Array<DaoICommunity>}>('/daoi/communities', signal),
				getDaoIData<{pinned_guild_ids: Array<string>}>('/daoi/conversation-preferences', signal),
			]).then((results) => {
				if (signal.aborted) return;
				if (results[0].status === 'fulfilled') {
					setCommunities(results[0].value.communities);
					setCommunitiesLoaded(true);
				}
				if (results[1].status === 'fulfilled') applyPinRead(results[1].value.pinned_guild_ids, pinRevision);
			});
		};
		if (previousGuildIds.current !== guildIds) reconcile();
		previousGuildIds.current = guildIds;
		window.addEventListener('focus', reconcile);
		return () => {
			controller?.abort();
			window.removeEventListener('focus', reconcile);
		};
	}, [userId, me?.wallet_address, guildIds, accountWorkAdmitted]);

	const setGuildPinned = (guildId: string, pinned: boolean): Promise<void> => {
		const state = pins.current;
		const generation = state.generation;
		const assertCurrent = () => {
			if (!userId || !state.active || state.generation !== generation || Users.currentUser?.id !== userId) {
				throw new Error('Conversation preference session changed');
			}
		};
		state.pending++;
		state.revision++;
		// Queue operations, not arrays computed from a render that may already be stale.
		const result = state.tail
			.then(async () => {
				assertCurrent();
				const response = await http.patch<{pinned_guild_ids: Array<string>}>('/daoi/conversation-preferences', {
					body: {guild_id: guildId, pinned},
				});
				assertCurrent();
				setPinnedGuildIds(response.body.pinned_guild_ids);
			})
			.finally(() => {
				state.pending--;
				state.revision++;
			});
		state.tail = result.catch(() => {});
		return result;
	};
	return (
		<DaoIContext.Provider
			value={{me, communities, communitiesLoaded, pinnedGuildIds, refresh, setGuildPinned, openBusiness, openMessages}}
		>
			{children}
		</DaoIContext.Provider>
	);
});

// This lives inside the original, shared GuildsLayout, so business drafts and
// the existing chat/voice runtime survive navigation between the four modules.
export const DaoIBusinessOutlet = observer(({children}: {children: ReactNode}) => {
	const {me, openBusiness} = useDaoI();
	const userId = Users.currentUser?.id;
	const location = useLocation();
	const view: DaoIBusinessView | null =
		location.pathname === DAOI_ROUTES.mint
			? 'mint'
			: location.pathname === DAOI_ROUTES.swap
				? 'swap'
				: location.pathname === DAOI_ROUTES.partner
					? 'partner'
					: null;
	const [opened, setOpened] = useState(false);
	const [lastView, setLastView] = useState<DaoIBusinessView>('mint');
	useEffect(() => {
		if (view) {
			setOpened(true);
			setLastView(view);
		}
	}, [view]);
	return (
		<>
			{children}
			{opened && (
				<DaoIBusinessHost
					key={userId}
					view={view ?? lastView}
					active={view !== null}
					theme={Theme.effectiveTheme === ThemeTypes.LIGHT ? 'light' : 'dark'}
					walletAddress={me?.wallet_address ?? null}
					onNavigate={openBusiness}
					data-flx="daoi.business-outlet.host"
				/>
			)}
		</>
	);
});
